import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { RootState } from "../../store"
import {
  API_STATUS,
  MODEL_META_DATA_CARDS,
  MODEL_META_DATA_KEY_MAPPER,
  MONTHS,
  MONTHS_DISPLAY_MAPPER,
  categoryAndRegionMapper,
  weekAndMonthMapperMaintenanceChart,
} from "../../util/StaticData"
import AxiosOperations from "../../services/AxiosOperations"
import { API } from "../../util/api"
import { IModelMetaDataCard } from "../../util/interface"
import { sortByYearAndMonth, sortByYearAndWeek } from "../../util/CommonMethods"
const tableData: any = []
const chartData: any = {
  categories: [],
  beforeRetrain: [],
  afterRetrain: [],
}
const totalRowTableData = {
  category: "Total",
  "HM CT_before_total": 0,
  OP_before_total: 0,
  "HM CC_before_total": 0,
  "HM CT_after_total": 0,
  OP_after_total: 0,
  "HM CC_after_total": 0,
  volume_mix: 0,
}
export const initialState = {
  status: API_STATUS.IDLE,
  tabs: MODEL_META_DATA_CARDS,
  activeTab: MODEL_META_DATA_KEY_MAPPER.MAPE,
  tableData,
  chartData,
  chartViewBySelectedValue: "month",
  tableViewBySelectedValue: "category",
  tableDataStatus: API_STATUS.IDLE,
  chartDataStatus: API_STATUS.IDLE,
  cardsDataStatus: API_STATUS.IDLE,
  totalRowTableData,
}
export const cardsAsync = createAsyncThunk(
  "ModelMetaData/cards",
  async (_, { getState }) => {
    try {
      const state: any = getState()
      const axiosObj = new AxiosOperations()
      const payload = {
        run_id: state.filters.runIdSelectedValue,
        use_case: state.filters.useCaseSelectedValue,
        market_place: state.filters.marketSingleCaseSelectedValue,
      }
      return axiosObj.get(API.MODEL_META_DATA_CARDS, payload)
    } catch (error) {
      console.error("Error:", error)
    }
  },
)
export const tableDataAsync = createAsyncThunk(
  "ModelMetaData/table",
  async (_, { getState }) => {
    try {
      const state: any = getState()

      const tiles_name = state.modelMetaData.tabs.find(
        (obj: IModelMetaDataCard) => obj.key == state.modelMetaData.activeTab,
      ).payloadKey
      const payload = {
        view_by:
          categoryAndRegionMapper[state.modelMetaData.tableViewBySelectedValue],
        tiles_name,
        run_id: state.filters.runIdSelectedValue,
        use_case: state.filters.useCaseSelectedValue,
        market_place: state.filters.marketSingleCaseSelectedValue,
      }
      const axiosObj = new AxiosOperations()
      return axiosObj.get(API.MODEL_META_DATA_TABLE, payload)
    } catch (error) {
      console.error("Error:", error)
    }
  },
)

export const chartDataAsync = createAsyncThunk(
  "ModelMetaData/chart",
  async (_, { getState }) => {
    try {
      const state: any = getState()

      const tiles_name = state.modelMetaData.tabs.find(
        (obj: IModelMetaDataCard) => obj.key == state.modelMetaData.activeTab,
      ).payloadKey
      const payload = {
        view_by:
          weekAndMonthMapperMaintenanceChart[
            state.modelMetaData.chartViewBySelectedValue
          ],
        tiles_name,
        run_id: state.filters.runIdSelectedValue,
        use_case: state.filters.useCaseSelectedValue,
        market_place: state.filters.marketSingleCaseSelectedValue,
      }
      const axiosObj = new AxiosOperations()
      return axiosObj.get(API.MODEL_META_DATA_CHART, payload)
    } catch (error) {
      console.error("Error:", error)
    }
  },
)

export const ModelMetaDataSlice = createSlice({
  name: "ModelMetaData",
  initialState,
  reducers: {
    tabChange: (state, action: PayloadAction<string>) => {
      state.activeTab = action.payload
    },
    chartViewByChange: (state, action: PayloadAction<string>) => {
      state.chartViewBySelectedValue = action.payload
    },
    tableViewByChange: (state, action: PayloadAction<string>) => {
      state.tableViewBySelectedValue = action.payload
    },
    resetState: (state) => {
      state.chartData = []
      state.tableData = []
      state.tabs = structuredClone(MODEL_META_DATA_CARDS)
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(cardsAsync.pending, (state) => {
        state.cardsDataStatus = API_STATUS.LOADING
      })
      .addCase(cardsAsync.fulfilled, (state, action: any) => {
        try {
          const afterRetrain = action.payload.data_after_retrain[0]
          const beforRetrain = action.payload.data_before_retrain[0]
          state.tabs = state.tabs.map((obj: IModelMetaDataCard) => {
            obj.percentage = afterRetrain[obj.payloadKey]
            obj.retrainingPercentage = beforRetrain[obj.payloadKey]
            return obj
          })

          console.log(state.tabs)
          state.cardsDataStatus = API_STATUS.IDLE
        } catch (error) {
          console.error(error)
        }
      })
      .addCase(cardsAsync.rejected, (state) => {
        state.cardsDataStatus = API_STATUS.FAILED
        state.tabs = structuredClone(MODEL_META_DATA_CARDS)
      })
      .addCase(tableDataAsync.pending, (state) => {
        state.tableDataStatus = API_STATUS.LOADING
      })
      .addCase(tableDataAsync.fulfilled, (state, action: any) => {
        try {
          state.tableData = action.payload.tableData
          state.totalRowTableData = state.tableData.find(
            (obj: any) => obj.CATEGORY_UPDATED === "Total",
          )
          state.tableData = state.tableData.filter(
            (obj: any) => obj.CATEGORY_UPDATED != "Total",
          )
          if (state.totalRowTableData) {
            state.totalRowTableData.volume_mix = calculateSum(
              state.tableData,
              "volume_mix",
            )
          }
          state.tableDataStatus = API_STATUS.IDLE
        } catch (error) {
          console.error(error)
        }
      })
      .addCase(tableDataAsync.rejected, (state) => {
        state.tableDataStatus = API_STATUS.FAILED
        state.tableData = []
      })
      .addCase(chartDataAsync.pending, (state) => {
        state.chartDataStatus = API_STATUS.LOADING
      })
      .addCase(chartDataAsync.fulfilled, (state, action: any) => {
        try {
          if (state.chartViewBySelectedValue === "week") {
            action.payload.data_after_retrain = sortByYearAndWeek(
              action.payload.data_after_retrain,
            )
            action.payload.data_before_retrain = sortByYearAndWeek(
              action.payload.data_before_retrain,
            )
          } else if (state.chartViewBySelectedValue === "month") {
            action.payload.data_after_retrain = sortByYearAndMonth(
              action.payload.data_after_retrain,
            )
            action.payload.data_before_retrain = sortByYearAndMonth(
              action.payload.data_before_retrain,
            )
          }
          action.payload.data_after_retrain =
            action.payload.data_after_retrain.sort(
              (a: any, b: any) =>
                parseFloat(a.Refresh_Year) - parseFloat(b.Refresh_Year),
            )
          action.payload.data_before_retrain =
            action.payload.data_before_retrain.sort(
              (a: any, b: any) =>
                parseFloat(a.Refresh_Year) - parseFloat(b.Refresh_Year),
            )

          let categories = action.payload.data_after_retrain.map(
            (obj: any) => `W${obj.Refresh_Month}-${obj.Refresh_Year}`,
          )
          const activeTabPayloadKey: any = state.tabs.find(
            (obj: any) => obj.key == state.activeTab,
          )?.payloadKey
          let beforeRetrain = action.payload.data_before_retrain.map(
            (obj: any) => obj[activeTabPayloadKey],
          )
          let afterRetrain = action.payload.data_after_retrain.map(
            (obj: any) => obj[activeTabPayloadKey],
          )
          if (state.chartViewBySelectedValue == "month") {
            categories = action.payload.data_after_retrain.map((obj: any) => {
              return `${
                MONTHS_DISPLAY_MAPPER[MONTHS[obj.Refresh_Month - 1]]
              }- ${obj.Refresh_Year}`
            })
          }
          beforeRetrain = beforeRetrain.map((val: number) =>
            Number(Number(val).toFixed(2)),
          )
          afterRetrain = afterRetrain.map((val: number) =>
            Number(Number(val).toFixed(2)),
          )

          state.chartData = {
            categories,
            beforeRetrain,
            afterRetrain,
          }
          state.chartDataStatus = API_STATUS.IDLE
        } catch (error) {
          console.error(error)
        }
      })
      .addCase(chartDataAsync.rejected, (state) => {
        state.chartDataStatus = API_STATUS.FAILED
        state.chartData = []
      })
  },
})
export const tabDataSelector = (state: RootState) => state.dataMetaData.tabs
export default ModelMetaDataSlice.reducer

export const { tabChange, chartViewByChange, tableViewByChange, resetState } =
  ModelMetaDataSlice.actions

function calculateSum(list: any, property: string) {
  if (list.length === 0) {
    return 0
  }

  const sum = list.reduce((total: any, item: any) => total + item[property], 0)
  return sum
}
